import { useQuery } from "@apollo/client";
import { filter, map, pipe, sortBy, uniqBy } from "lodash/fp";
import { Suspense, useMemo, useState } from "react";

import isNotNullish from "lib/isNotNullish";
import Suspender from "lib/Suspender";
import {
  WorkspaceRetailerNamesDoc,
  WorkspaceRetailerNamesQuery,
} from "operations";

import {
  LoadingSkeleton,
  PrimaryPill,
  SecondaryPill,
} from "@joor/design-system";
import {
  StyledContent,
  StyledLabel,
  StyledMenu,
  StyledMenuOptions,
  StyledTitle,
} from "./styledComponents";

const mapRetailers = pipe(
  (data?: WorkspaceRetailerNamesQuery) => data?.retailWorkspace?.assortments,
  map((assortment) => assortment.retailer.node),
  filter(isNotNullish),
  uniqBy((retailer) => retailer.id),
  map(
    (retailer) => retailer.displayName || retailer.name || "Unknown Retailer"
  ),
  sortBy((value) => value)
);

const useRetailers = (workspaceId: string) => {
  const { data, loading } = useQuery(WorkspaceRetailerNamesDoc, {
    variables: { workspaceId: workspaceId },
  });
  const retailers = useMemo(() => data && mapRetailers(data), [data]);
  return { loading, retailers };
};

export const Retailers = ({ workspaceId }: { workspaceId: string }) => {
  const { loading, retailers } = useRetailers(workspaceId);
  const [showRetailers, setShowRetailers] = useState(false);
  const onHover = () => {
    setShowRetailers(true);
  };
  const onLeave = () => {
    setShowRetailers(false);
  };

  const noRetailer = !retailers || retailers?.length === 0;
  const oneRetailer = retailers && retailers?.length === 1;
  const multipleRetailers = retailers && retailers?.length > 1;

  return (
    <StyledContent>
      <Suspense fallback={<LoadingSkeleton />}>
        <Suspender suspend={loading}>
          <StyledLabel>Retailer</StyledLabel>
          {multipleRetailers && (
            <StyledTitle data-testid="retailers-value">
              All Retailers
              <div onMouseEnter={onHover} onMouseLeave={onLeave}>
                {showRetailers ? (
                  <>
                    <SecondaryPill className="hover" variant="INACTIVE">
                      {retailers.length}
                    </SecondaryPill>
                    <StyledMenu>
                      {retailers.map((retailers) => (
                        <StyledMenuOptions>
                          <PrimaryPill variant="INACTIVE">
                            {retailers}
                          </PrimaryPill>
                        </StyledMenuOptions>
                      ))}
                    </StyledMenu>
                  </>
                ) : (
                  <PrimaryPill className="hover" variant="INACTIVE">
                    {retailers.length}
                  </PrimaryPill>
                )}
              </div>
            </StyledTitle>
          )}
          {oneRetailer && (
            <StyledTitle data-testid="retailers-value">
              {retailers[0]}
            </StyledTitle>
          )}
          {noRetailer && (
            <StyledTitle data-testid="retailers-value">-</StyledTitle>
          )}
        </Suspender>
      </Suspense>
    </StyledContent>
  );
};
